import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useNotificationStore } from './notificationStore'

// User roles
export type UserRole = 'super_admin' | 'owner' | 'administrator' | 'it_user' | 'viewer'

// Tenant types
export type TenantType = 'standard' | 'msp' | 'enterprise'

// User information from JWT
export interface User {
  id: string
  username: string
  email: string
  full_name: string
  avatar_url?: string
  is_super_admin: boolean
  sso_provider?: string
  /** Whether the account is protected by a second factor. Read from the row by /auth/me. */
  mfa_enabled?: boolean
}

// Current tenant context from JWT
export interface TenantContext {
  tenant_id: string | null
  tenant_name: string
  tenant_type: TenantType | 'platform'
  role: UserRole
  permissions: string[]
}

// Accessible tenant for tenant switching
export interface AccessibleTenant {
  id: string
  name: string
  display_name: string
  type: TenantType
  role: UserRole
  is_primary: boolean
  is_managed: boolean  // True if accessed via MSP relationship
}

// Login response from API
export interface LoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: User
  tenant_context: TenantContext
  accessible_tenants: AccessibleTenant[]
}

interface AuthStore {
  // Authentication state
  user: User | null
  token: string | null
  isAuthenticated: boolean

  // Multi-tenant state
  tenantContext: TenantContext | null
  accessibleTenants: AccessibleTenant[]

  // Actions
  login: (response: LoginResponse) => void
  logout: () => void
  switchTenant: (response: LoginResponse) => void

  // Update accessible tenants (e.g., after adding/removing managed tenants)
  updateAccessibleTenants: (tenants: AccessibleTenant[]) => void

  // Fold fresh user fields in without re-authenticating. The account page changes the display
  // name and the MFA state while the token that carries the old values is still valid.
  updateUser: (patch: Partial<User>) => void

  // Computed helpers
  getCurrentTenantId: () => string | null
  getCurrentRole: () => UserRole | null
  hasPermission: (permission: string) => boolean
  isTenantAdmin: () => boolean
  canManageSubscription: () => boolean
  isSuperAdmin: () => boolean
  canManageApps: () => boolean
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      tenantContext: null,
      accessibleTenants: [],

      // Login action - stores token and all tenant context
      login: (response: LoginResponse) => set({
        token: response.access_token,
        user: response.user,
        tenantContext: response.tenant_context,
        accessibleTenants: response.accessible_tenants,
        isAuthenticated: true,
      }),

      // Logout action - clears all state
      logout: () => {
        useNotificationStore.getState().clear()
        set({
          token: null,
          user: null,
          tenantContext: null,
          accessibleTenants: [],
          isAuthenticated: false,
        })
      },

      // Switch tenant - updates token and context
      switchTenant: (response: LoginResponse) => set({
        token: response.access_token,
        tenantContext: response.tenant_context,
        accessibleTenants: response.accessible_tenants,
        // User info stays the same
      }),

      // Update accessible tenants list without changing token
      updateAccessibleTenants: (tenants: AccessibleTenant[]) => set({
        accessibleTenants: tenants,
      }),

      // Merge fresh user fields into the stored user, leaving the token alone.
      updateUser: (patch: Partial<User>) => set((state) => ({
        user: state.user ? { ...state.user, ...patch } : state.user,
      })),

      // Get current tenant ID
      getCurrentTenantId: () => {
        const state = get()
        return state.tenantContext?.tenant_id || null
      },

      // Get current role in the tenant context
      getCurrentRole: () => {
        const state = get()
        return state.tenantContext?.role || null
      },

      // Check if user has a specific permission
      hasPermission: (permission: string) => {
        const state = get()
        return state.tenantContext?.permissions?.includes(permission) || false
      },

      // Whether the user administers the tenant. An owner is an administrator that also holds the
      // subscription, so it passes everything an administrator passes.
      isTenantAdmin: () => {
        const state = get()
        const role = state.tenantContext?.role
        return role === 'administrator' || role === 'owner' || role === 'super_admin'
      },

      // Whether the user may buy, resize or cancel the subscription. The one authority that
      // separates an owner from an administrator.
      canManageSubscription: () => {
        const state = get()
        const role = state.tenantContext?.role
        return role === 'owner' || role === 'super_admin'
      },

      // Check if user is super admin
      isSuperAdmin: () => {
        const state = get()
        return state.user?.is_super_admin || state.tenantContext?.role === 'super_admin'
      },

      // Check if user can manage apps
      canManageApps: () => {
        const state = get()
        return state.tenantContext?.permissions?.includes('manage_apps') || false
      },
    }),
    {
      name: 'auth-storage',
    }
  )
)
